import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchStateDetailMap } from '../../api/states';
import { fetchPartnerMap, PartnerMapData } from '../../api/partnerMap';
import { useStateMap } from '../../hooks/useStateMap';
import PartnerResultsMap, {
  PartnerMapConfig, PartnerSectionHead, usePartnerFilter,
} from './PartnerResultsMap';
import { STATE_PATH } from './StateTemplatePage';

type PartnerMapProps = {
  eyebrow?: string;
  heading: string;
  lede?: string;
  /** Icon key for every partner pin — one shared style, not per-partner
   * (see docs/plans/ed-partners.md's "Marker styling" decision). */
  markerIcon?: string;
  /** Name of a live choice-type FormField on the tenant's `partner` form
   * (see partnersApi.filterableFields) to offer as an end-visitor filter
   * over the results. Empty/unset shows every partner with no filter UI. */
  filterField?: string;
};

/**
 * Published, geocoded partners inside the state the current
 * `/pages/state/<abbr>/` route names (`GET /api/partners/<abbr>/map/`, see
 * `api/partnerMap.ts`), laid out like a map search by the shared
 * `PartnerResultsMap`. The map beneath the pins is the same single-state frame
 * `StateTemplatePage` falls back to (`fetchStateDetailMap`).
 */
export default function PartnerMap({ eyebrow, heading, lede, markerIcon, filterField }: PartnerMapProps) {
  const location = useLocation();
  const abbreviation = useMemo(
    () => location.pathname.match(STATE_PATH)?.[1],
    [location.pathname],
  );

  const stateRequest = useStateMap(
    (signal) =>
      abbreviation
        ? fetchStateDetailMap(abbreviation, signal)
        : Promise.reject(new Error('PartnerMap rendered outside a /pages/state/<abbr>/ route')),
    [abbreviation],
  );
  const partnerRequest = useStateMap<PartnerMapData>(
    (signal) =>
      abbreviation
        ? fetchPartnerMap(abbreviation, filterField, signal)
        : Promise.reject(new Error('PartnerMap rendered outside a /pages/state/<abbr>/ route')),
    [abbreviation, filterField],
  );
  const partnerData = partnerRequest.status === 'ready' ? partnerRequest.data : undefined;
  const mapData = stateRequest.status === 'ready' ? stateRequest.data : undefined;
  const feature = mapData?.features[0];

  const { filterValues, activeFilter, setActiveFilter, filtered } = usePartnerFilter(
    partnerData?.pins ?? [], `${abbreviation}|${filterField}`,
  );
  const pins = useMemo(
    () => [...filtered].sort((a, b) => a.school_name.localeCompare(b.school_name)),
    [filtered],
  );

  return (
    <section className="states partner-map section">
      <div className="wrap">
        <PartnerSectionHead eyebrow={eyebrow} heading={heading} lede={lede} />
        <PartnerMapConfig testIdPrefix="partner-map" markerIcon={markerIcon} filterField={filterField} />
        <PartnerResultsMap
          testIdPrefix="partner-map"
          pins={pins}
          loaded={!!partnerData}
          emptyMessage={
            partnerRequest.status === 'loading'
              ? 'Loading schools…'
              : partnerRequest.status === 'ready'
                ? `No partner schools${feature ? ` in ${feature.properties.full_name}` : ''} yet.`
                : 'Partner schools are unavailable right now.'
          }
          mapData={mapData && feature ? mapData : undefined}
          mapPlaceholder={
            stateRequest.status === 'error'
              ? 'The map is unavailable right now.'
              : stateRequest.status === 'missing'
                ? 'This state does not currently have a page.'
                : 'Loading map…'
          }
          mapAriaLabel={`Map of partner schools in ${feature?.properties.full_name ?? 'this state'}`}
          markerIcon={markerIcon}
          renderAddress={(pin) => [pin.street, pin.city].filter(Boolean).join(', ')}
          controlsAfter={
            filterField && filterValues.length > 0 ? (
              <select
                aria-label={partnerData?.filter_field_label ?? 'Filter'}
                data-testid="partner-map-filter-select"
                value={activeFilter}
                onChange={(event) => setActiveFilter(event.target.value)}
              >
                <option value="">{`${partnerData?.filter_field_label ?? 'Filter'}: All`}</option>
                {filterValues.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            ) : null
          }
        />
      </div>
    </section>
  );
}
